import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Download, Search, X } from 'lucide-react';

import { apiDownload } from '@/lib/api/client';
import {
  buildSubscriptionsCsvUrl,
  useAdminSubscriptions,
  useProductLabels,
  type SubscriptionsFilter,
} from '@/lib/api/adminHooks';

import { SyncStatusBar } from './SyncStatusBar';

const PAGE_SIZE = 50;

type StatusOpt = 'active' | 'cancelled' | 'paused' | 'payment_failed' | null;

function parseStatusParam(raw: string | null): StatusOpt {
  if (
    raw === 'active' ||
    raw === 'cancelled' ||
    raw === 'paused' ||
    raw === 'payment_failed'
  )
    return raw;
  return null;
}

function dateOnly(iso: string | null): string {
  if (!iso) return '';
  // Accept both full ISO and YYYY-MM-DD shapes.
  return iso.slice(0, 10);
}

export function SubscriptionsScreen() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Cohort drill-down params — set by the CohortHeatmap "View ..." links.
  // These have no UI control on this page; we show a banner + Clear button
  // that wipes them from the URL when the user wants to exit the drill-down.
  const paidRenewalsMin = (() => {
    const raw = searchParams.get('paid_renewals_min');
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  })();
  const paidRenewalsMax = (() => {
    const raw = searchParams.get('paid_renewals_max');
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  })();
  const cancelWeek = (() => {
    const raw = searchParams.get('cancel_week');
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  })();

  const [statusFilter, setStatusFilter] = useState<StatusOpt>(
    parseStatusParam(searchParams.get('status'))
  );
  const [productLabel, setProductLabel] = useState<string>(
    searchParams.get('product_label') ?? ''
  );
  const [pendingSearch, setPendingSearch] = useState('');
  const [search, setSearch] = useState('');
  // Stored as the raw filter value (date string OR full ISO timestamp). This
  // preserves URL-supplied ISO timestamps from cohort drill-down links — those
  // encode UTC Monday-to-Monday boundaries that must NOT be re-interpreted in
  // the viewer's local timezone (which would shift them 7h east on PT and
  // sweep the next cohort's signups into this cohort's list).
  // The date picker reads `dateOnly(...)` for display and writes back a
  // local-time-interpreted ISO so manual picking still aligns with the
  // daily-signups chart's PT-bucketed days.
  const [createdAfter, setCreatedAfter] = useState(
    searchParams.get('created_after') ?? ''
  );
  const [createdBefore, setCreatedBefore] = useState(
    searchParams.get('created_before') ?? ''
  );
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  const clearCohortDrilldown = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('paid_renewals_min');
    next.delete('paid_renewals_max');
    next.delete('cancel_week');
    setSearchParams(next, { replace: true });
  };

  const filter = useMemo<SubscriptionsFilter>(
    () => ({
      status: statusFilter,
      productLabel: productLabel || null,
      search: search || null,
      // Stored value is already either a date string from the picker
      // (local-time-interpreted to ISO at write time) or a full ISO from a
      // cohort drill-down URL — pass it through unchanged.
      createdAfter: createdAfter || null,
      createdBefore: createdBefore || null,
      minPrice: minPrice ? Number(minPrice) : null,
      maxPrice: maxPrice ? Number(maxPrice) : null,
      paidRenewalsMin: paidRenewalsMin,
      paidRenewalsMax: paidRenewalsMax,
      cancelWeek: cancelWeek,
    }),
    [
      statusFilter,
      productLabel,
      search,
      createdAfter,
      createdBefore,
      minPrice,
      maxPrice,
      paidRenewalsMin,
      paidRenewalsMax,
      cancelWeek,
    ]
  );

  const { data, isLoading, isError } = useAdminSubscriptions(filter, {
    limit: PAGE_SIZE,
    offset,
  });
  const { data: productLabels } = useProductLabels();

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const handleExport = async () => {
    setExporting(true);
    try {
      const url = buildSubscriptionsCsvUrl(filter);
      const stamp = new Date().toISOString().slice(0, 10);
      await apiDownload(url, `subscriptions-${stamp}.csv`);
    } catch (e) {
      alert(`Export failed: ${String(e)}`);
    } finally {
      setExporting(false);
    }
  };

  const anyAdvancedFilter =
    productLabel || createdAfter || createdBefore || minPrice || maxPrice;

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">Subscriptions</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          Combined view of CarePortals and new-portal (Stripe) subscriptions.
          Click a CP row to see the customer's full history. The Export button
          downloads the full filtered result set as CSV.
        </p>
      </div>

      <SyncStatusBar />

      {(paidRenewalsMin != null || paidRenewalsMax != null || cancelWeek != null) && (
        <div className="p-12 border border-line rounded-lg bg-sunken flex items-center gap-12 type-label-sm">
          <span className="text-ink font-medium">Cohort drill-down active</span>
          <span className="text-ink-muted">
            {paidRenewalsMin != null && (
              <>Retained to M{paidRenewalsMin}+ (≥{paidRenewalsMin} paid renewals)</>
            )}
            {paidRenewalsMax != null && (
              <>
                Churned by M{paidRenewalsMax + 1} (≤{paidRenewalsMax} paid renewals)
              </>
            )}
            {cancelWeek != null && (
              <>
                Cancelled in W{cancelWeek} (days {cancelWeek * 7}–{(cancelWeek + 1) * 7} post-signup)
              </>
            )}
          </span>
          <button
            type="button"
            onClick={clearCohortDrilldown}
            className="ml-auto inline-flex items-center gap-4 px-8 py-4 border border-line rounded hover:bg-raised text-ink-muted hover:text-ink"
          >
            <X size={12} strokeWidth={1.5} />
            Clear drill-down
          </button>
        </div>
      )}

      <div className="p-12 border border-line rounded-lg flex flex-col gap-12">
        <div className="flex flex-wrap items-center gap-12">
          <Filter
            label="Status"
            options={[
              { label: 'All', value: null },
              { label: 'Active', value: 'active' },
              { label: 'Payment failed', value: 'payment_failed' },
              { label: 'Cancelled', value: 'cancelled' },
              { label: 'Paused', value: 'paused' },
            ]}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setOffset(0);
            }}
          />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(pendingSearch);
              setOffset(0);
            }}
            className="flex items-center gap-6 type-label-sm"
          >
            <label className="flex items-center gap-6 bg-raised border border-line rounded px-8 py-4">
              <Search size={14} strokeWidth={1.5} className="text-ink-muted" />
              <input
                type="text"
                placeholder="Search email, product, customer id"
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                className="bg-transparent outline-none w-[240px]"
              />
            </label>
            <button
              type="submit"
              className="px-10 py-4 border border-line rounded hover:bg-raised"
            >
              Search
            </button>
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setPendingSearch('');
                  setOffset(0);
                }}
                className="px-10 py-4 text-ink-muted hover:text-ink"
              >
                Clear
              </button>
            )}
          </form>

          <div className="ml-auto flex items-center gap-12 type-label-sm">
            <span className="text-ink-muted">
              {data ? `${data.total.toLocaleString()} results` : '…'}
            </span>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-6 px-12 py-6 rounded border border-line hover:bg-raised disabled:opacity-50"
            >
              <Download size={14} strokeWidth={1.5} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-12 type-label-sm">
          <label className="flex items-center gap-6">
            <span className="text-ink-muted">Product</span>
            <select
              value={productLabel}
              onChange={(e) => {
                setProductLabel(e.target.value);
                setOffset(0);
              }}
              className="bg-raised border border-line rounded px-8 py-4"
            >
              <option value="">All products</option>
              {(productLabels ?? []).map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-6">
            <span className="text-ink-muted">Created after</span>
            <input
              type="date"
              value={dateOnly(createdAfter)}
              onChange={(e) => {
                setCreatedAfter(
                  e.target.value
                    ? new Date(`${e.target.value}T00:00:00`).toISOString()
                    : ''
                );
                setOffset(0);
              }}
              className="bg-raised border border-line rounded px-8 py-4"
            />
          </label>

          <label className="flex items-center gap-6">
            <span className="text-ink-muted">Before</span>
            <input
              type="date"
              value={dateOnly(createdBefore)}
              onChange={(e) => {
                setCreatedBefore(
                  e.target.value
                    ? new Date(`${e.target.value}T23:59:59.999`).toISOString()
                    : ''
                );
                setOffset(0);
              }}
              className="bg-raised border border-line rounded px-8 py-4"
            />
          </label>

          <label className="flex items-center gap-6">
            <span className="text-ink-muted">Price</span>
            <span className="text-ink-muted">$</span>
            <input
              type="number"
              min={0}
              step="1"
              placeholder="min"
              value={minPrice}
              onChange={(e) => {
                setMinPrice(e.target.value);
                setOffset(0);
              }}
              className="w-[80px] bg-raised border border-line rounded px-8 py-4"
            />
            <span className="text-ink-muted">–</span>
            <span className="text-ink-muted">$</span>
            <input
              type="number"
              min={0}
              step="1"
              placeholder="max"
              value={maxPrice}
              onChange={(e) => {
                setMaxPrice(e.target.value);
                setOffset(0);
              }}
              className="w-[80px] bg-raised border border-line rounded px-8 py-4"
            />
          </label>

          {anyAdvancedFilter && (
            <button
              type="button"
              onClick={() => {
                setProductLabel('');
                setCreatedAfter('');
                setCreatedBefore('');
                setMinPrice('');
                setMaxPrice('');
                setOffset(0);
              }}
              className="px-10 py-4 text-ink-muted hover:text-ink ml-auto"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {isError && (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md">
          Failed to load subscriptions.
        </div>
      )}

      <div className="border border-line rounded-lg bg-raised overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full type-label-sm">
            <thead className="bg-sunken border-b border-line">
              <tr className="text-left text-ink-muted">
                <Th>Customer</Th>
                <Th>Source</Th>
                <Th>Product</Th>
                <Th>Status</Th>
                <Th align="right">Price</Th>
                <Th align="right">Cycle</Th>
                <Th>Created</Th>
                <Th>Next cycle</Th>
                <Th>Cancelled</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading && !data && (
                <tr>
                  <td colSpan={9} className="p-16 text-center text-ink-muted">
                    Loading…
                  </td>
                </tr>
              )}
              {data?.data.map((row) => (
                <tr
                  key={row.cp_id}
                  className="border-t border-line-subtle hover:bg-sunken"
                >
                  <Td>
                    <span className="inline-flex items-center gap-6">
                      {row.source === 'portal' && row.patient_id ? (
                        <Link
                          to={`/admin/clinical-admin/customers/${row.patient_id}`}
                          className="underline decoration-line underline-offset-4 hover:decoration-ink"
                        >
                          {row.customer_email || row.patient_id}
                        </Link>
                      ) : row.customer_cp_id && row.source !== 'portal' ? (
                        <Link
                          to={`/admin/customers/${row.customer_cp_id}`}
                          className="underline decoration-line underline-offset-4 hover:decoration-ink"
                        >
                          {row.customer_email || row.customer_cp_id}
                        </Link>
                      ) : (
                        row.customer_email || <span className="text-ink-muted">—</span>
                      )}
                      {row.is_internal && <TestBadge />}
                    </span>
                  </Td>
                  <Td>
                    <SourceBadge source={row.source} />
                  </Td>
                  <Td>{row.product_label || '—'}</Td>
                  <Td>
                    {row.status ? (
                      <StatusBadge
                        status={row.status}
                        latestRenewalStatus={row.latest_renewal_status}
                        pausedReason={row.paused_reason}
                      />
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td align="right">
                    {row.first_cycle_price_cents != null
                      ? formatCents(row.first_cycle_price_cents)
                      : '—'}
                  </Td>
                  <Td align="right">{row.current_cycle ?? '—'}</Td>
                  <Td>
                    {row.created_at
                      ? format(parseISO(row.created_at), 'MMM d, yyyy')
                      : '—'}
                  </Td>
                  <Td>
                    {row.next_cycle_date
                      ? format(parseISO(row.next_cycle_date), 'MMM d, yyyy')
                      : '—'}
                  </Td>
                  <Td>
                    {row.cancelled_at ? (
                      <span className="text-danger">
                        {format(parseISO(row.cancelled_at), 'MMM d, yyyy')}
                      </span>
                    ) : (
                      '—'
                    )}
                  </Td>
                </tr>
              ))}
              {data?.data.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-16 text-center text-ink-muted">
                    No subscriptions match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-12 p-12 border-t border-line type-label-sm">
          <span className="text-ink-muted">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex items-center gap-8">
            <button
              type="button"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="px-10 py-4 border border-line rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() =>
                setOffset(
                  data && offset + PAGE_SIZE < data.total ? offset + PAGE_SIZE : offset
                )
              }
              disabled={!data || offset + PAGE_SIZE >= data.total}
              className="px-10 py-4 border border-line rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`px-12 py-8 font-medium ${
        align === 'right' ? 'text-right' : ''
      }`}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <td className={`px-12 py-8 ${align === 'right' ? 'text-right' : ''}`}>{children}</td>
  );
}

function StatusBadge({
  status,
  latestRenewalStatus,
  pausedReason,
}: {
  status: string;
  latestRenewalStatus?: string | null;
  pausedReason?: string | null;
}) {
  const lower = status.toLowerCase();
  // Cancelled wins over everything — terminal state.
  if (lower === 'cancelled' || lower === 'canceled') {
    return (
      <span className="inline-block px-8 py-2 rounded text-11 uppercase tracking-wide bg-danger-wash text-danger">
        {status}
      </span>
    );
  }
  // Paused covers both CP-native status='paused' and active subs whose
  // latest renewalHistory entry is skip_cycle. Surface the same badge for
  // either so we stop counting paused customers as churned.
  if (pausedReason) {
    const tip =
      pausedReason === 'skip_cycle'
        ? 'Customer skipped this cycle in CarePortals'
        : pausedReason === 'update_renewal_date'
          ? 'Customer pushed back their renewal date in the patient portal'
          : 'Subscription paused in CarePortals';
    return (
      <span
        className="inline-block px-8 py-2 rounded text-11 uppercase tracking-wide bg-raised text-ink-muted border border-line"
        title={tip}
      >
        Paused
      </span>
    );
  }
  // CP keeps subs `active` while retrying failed charges — surface dunning.
  if (lower === 'active' && latestRenewalStatus === 'failed') {
    return (
      <span className="inline-block px-8 py-2 rounded text-11 uppercase tracking-wide bg-danger-wash text-danger">
        Payment failed
      </span>
    );
  }
  const tone =
    lower === 'active'
      ? 'bg-ink text-ink-onAccent'
      : 'bg-raised text-ink-muted';
  return (
    <span
      className={`inline-block px-8 py-2 rounded text-11 uppercase tracking-wide ${tone}`}
    >
      {status}
    </span>
  );
}

function TestBadge() {
  return (
    <span
      className="inline-block px-6 py-1 rounded text-10 uppercase tracking-wide bg-raised text-ink-muted border border-line"
      title="Internal/test account (@systemlabs.com) — excluded from all aggregate metrics"
    >
      Test
    </span>
  );
}

function SourceBadge({ source }: { source?: string | null }) {
  if (source === 'portal') {
    return (
      <span
        className="inline-block px-6 py-1 rounded text-10 uppercase tracking-wide bg-ink text-ink-onAccent"
        title="New-portal subscription managed via Stripe"
      >
        Portal
      </span>
    );
  }
  return (
    <span
      className="inline-block px-6 py-1 rounded text-10 uppercase tracking-wide bg-raised text-ink-muted border border-line"
      title="CarePortals-managed subscription"
    >
      CP
    </span>
  );
}

function Filter<T extends string | null>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-8 type-label-sm">
      <span className="text-ink-muted">{label}</span>
      <div className="flex rounded border border-line overflow-hidden">
        {options.map((o) => (
          <button
            key={o.value ?? '__null__'}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-10 py-6 ${
              value === o.value
                ? 'bg-ink text-ink-onAccent'
                : 'bg-raised hover:bg-sunken text-ink-muted'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
